// src/features/dossiers/components/steps/StepVoyageurs.jsx
import React, { useState } from 'react';
import {
    Box, Typography, Stack, TextField, Button,
    IconButton, Collapse, alpha,
} from '@mui/material';
import {
    PersonAddOutlined, DeleteOutlined,
    ExpandMore, ExpandLess, Person,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', fontSize: '0.86rem',
        fontFamily: "'DM Sans', sans-serif",
        '& fieldset': { borderColor: 'rgba(26,26,46,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.4)' },
        '&.Mui-focused fieldset': { borderColor: '#c9a84c', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#c9a84c' },
};

function FieldLabel({ children, required }) {
    return (
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1a1a2e', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.7 }}>
            {children}{required && <Box component="span" sx={{ color: '#c9a84c', ml: 0.3 }}>*</Box>}
        </Typography>
    );
}

const EMPTY_VOYAGEUR = () => ({
    tempId: uuidv4(),
    nom: '', prenom: '', dateNaissance: '', numeroPasseport: '', dateExpirationPasseport: '',
});

export function StepVoyageurs({ data, onChange }) {
    const [expanded, setExpanded] = useState(data.voyageurs?.length > 0 ? data.voyageurs[0].tempId : null);

    const voyageurs = data.voyageurs || [];

    const addVoyageur = () => {
        const v = EMPTY_VOYAGEUR();
        const updated = [...voyageurs, v];
        onChange({ voyageurs: updated });
        setExpanded(v.tempId);
    };

    const removeVoyageur = (tempId) => {
        const updated = voyageurs.filter(v => v.tempId !== tempId);
        onChange({ voyageurs: updated });
        // Retirer ce voyageur des bénéficiaires de toutes les prestations
        const prestations = (data.prestations || []).map(p => ({
            ...p,
            beneficiairesTempIds: (p.beneficiairesTempIds || []).filter(id => id !== tempId),
        }));
        onChange({ voyageurs: updated, prestations });
    };

    const updateVoyageur = (tempId, field, value) => {
        const updated = voyageurs.map(v => v.tempId === tempId ? { ...v, [field]: value } : v);
        onChange({ voyageurs: updated });
    };

    const getInitials = (v) => {
        const name = `${v.prenom} ${v.nom}`.trim();
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    const isComplete = (v) => v.nom && v.prenom && v.dateNaissance && v.numeroPasseport;

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>
                    Voyageurs
                </Typography>
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: '8px', bgcolor: '#1a1a2e' }}>
                    <Typography sx={{ color: '#c9a84c', fontSize: '0.7rem', fontWeight: 800 }}>{voyageurs.length}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'rgba(26,26,46,0.07)' }} />
            </Stack>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
                {voyageurs.map((v, index) => {
                    const open = expanded === v.tempId;
                    const complete = isComplete(v);
                    return (
                        <Box
                            key={v.tempId}
                            sx={{
                                borderRadius: '16px', overflow: 'hidden',
                                border: '1px solid',
                                borderColor: open ? 'rgba(201,168,76,0.35)' : 'rgba(26,26,46,0.08)',
                                transition: 'border-color 0.2s',
                            }}
                        >
                            {/* Header de la carte voyageur */}
                            <Stack direction="row" alignItems="center" spacing={1.5}
                                onClick={() => setExpanded(open ? null : v.tempId)}
                                sx={{ p: 2, cursor: 'pointer', bgcolor: open ? 'rgba(201,168,76,0.03)' : 'white', '&:hover': { bgcolor: 'rgba(201,168,76,0.03)' }, userSelect: 'none' }}
                            >
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: complete ? 'rgba(201,168,76,0.12)' : 'rgba(26,26,46,0.05)', border: complete ? '1px solid rgba(201,168,76,0.3)' : '1px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {complete
                                        ? <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#c9a84c' }}>{getInitials(v)}</Typography>
                                        : <Person sx={{ fontSize: 16, color: 'rgba(26,26,46,0.3)' }} />
                                    }
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>
                                        {v.prenom || v.nom ? `${v.prenom} ${v.nom}`.trim() : `Voyageur ${index + 1}`}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.4)' }}>
                                        {complete ? `Passeport · ${v.numeroPasseport}` : 'Informations incomplètes'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    {!complete && (
                                        <Box sx={{ px: 1, py: 0.2, borderRadius: '6px', bgcolor: 'rgba(248,113,113,0.1)' }}>
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#f87171' }}>Incomplet</Typography>
                                        </Box>
                                    )}
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); removeVoyageur(v.tempId); }}
                                        sx={{ color: 'rgba(26,26,46,0.25)', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' } }}>
                                        <DeleteOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    {open ? <ExpandLess sx={{ fontSize: 18, color: 'rgba(26,26,46,0.4)' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'rgba(26,26,46,0.4)' }} />}
                                </Stack>
                            </Stack>

                            {/* Formulaire dépliable */}
                            <Collapse in={open}>
                                <Box sx={{ p: 2.5, pt: 0, bgcolor: 'white', borderTop: '1px solid rgba(26,26,46,0.06)' }}>
                                    <Box sx={{ pt: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <Box>
                                            <FieldLabel required>Prénom</FieldLabel>
                                            <TextField fullWidth size="small" placeholder="Marie" value={v.prenom} onChange={e => updateVoyageur(v.tempId, 'prenom', e.target.value)} sx={FIELD_SX} />
                                        </Box>
                                        <Box>
                                            <FieldLabel required>Nom</FieldLabel>
                                            <TextField fullWidth size="small" placeholder="Dupont" value={v.nom} onChange={e => updateVoyageur(v.tempId, 'nom', e.target.value)} sx={FIELD_SX} />
                                        </Box>
                                        <Box>
                                            <FieldLabel required>Date de naissance</FieldLabel>
                                            <TextField fullWidth size="small" type="date" value={v.dateNaissance} onChange={e => updateVoyageur(v.tempId, 'dateNaissance', e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
                                        </Box>
                                        <Box>
                                            <FieldLabel required>N° Passeport</FieldLabel>
                                            <TextField fullWidth size="small" placeholder="AB123456" value={v.numeroPasseport} onChange={e => updateVoyageur(v.tempId, 'numeroPasseport', e.target.value)} sx={FIELD_SX} />
                                        </Box>
                                        <Box>
                                            <FieldLabel>Expiration passeport</FieldLabel>
                                            <TextField fullWidth size="small" type="date" value={v.dateExpirationPasseport} onChange={e => updateVoyageur(v.tempId, 'dateExpirationPasseport', e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>

            {/* Bouton ajouter */}
            <Button
                onClick={addVoyageur}
                startIcon={<PersonAddOutlined sx={{ fontSize: '17px !important' }} />}
                fullWidth
                sx={{
                    py: 1.5, borderRadius: '14px',
                    border: '1.5px dashed rgba(26,26,46,0.15)',
                    color: 'rgba(26,26,46,0.5)', bgcolor: 'transparent',
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                    textTransform: 'none',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#c9a84c', color: '#c9a84c', bgcolor: 'rgba(201,168,76,0.03)' },
                }}
            >
                Ajouter un voyageur
            </Button>
        </Box>
    );
}